import { TFunction } from 'react-i18next';
import i18n from 'i18next';
import type { ToastPosition } from 'react-toastify';
import {
    cancelTournamentScheduleGeneration,
    createTournamentSchedule,
    fetchTournamentWithClassAndCourtListByTournamentId,
    getScheduleConstraints,
    getScheduleJobStatus,
    getTournamentSchedule,
    publishTournamentSchedule,
    removeTournamentSchedule,
    updateTournamentSchedule,
} from '../../apis/tournament';
import { AppDispatch, RootState } from '../../app/store';
import { Notification, scheduleActionInfoWithUndo, scheduleActionSuccessWithUndo } from '../../hooks/useNotification';
import { TournamentMatchStatusEnum, TypeError } from '../../types/enums';
import type { TournamentState, Court } from '../../types/tournament';
import {
    IFreeSlotItem,
    IMatchScheduleItem,
    IMatchScheduleItemDto,
    ITournamentSchedule,
    isFreeSlot,
    isScheduled,
    IScheduleConstraints
} from '../../types/schedule';
import { selectSchedule } from './scheduleSelectors';
import {
    initialDataFetchError,
    initialDataFetchSuccess,
    initialDataFetching,
    updateSchedule,
    updateScheduleSuccess,
    updateScheduleError,
    setSelectedItem,
    setScheduleState,
    setUnscheduledMatchesState,
    creatingSchedule,
    creatingScheduleError,
    creatingScheduleSuccess,
    setScheduleConstraintsGiven,
    setTournamentInfo,
    setScheduleConstraints,
    restoreInitialState,
    setLoadedSavedSchedule,
    publishSchedule,
    publishScheduleSuccess,
    publishScheduleError,
    setScheduleStateFromSource,
    setGeneratingAsync,
    clearScheduleStates,
    setJobProgress,
    applyScheduleAndUnscheduled,
    pushScheduleHistoryBeforeEdit,
    undoScheduleChange,
    discardLastScheduleUndoSnapshot,
    clearScheduleSelection,
} from './scheduleSlice';
import { snapshotScheduleState } from './scheduleHistory';
import { generateDefaultSchedulingConstraints } from './schedule.helpers';
import {
    getTournamentTimezone,
    normalizeScheduleWithConstraints,
    rebuildEntireSchedule,
    getMatchDurationFromConstraints,
    resolveTournamentDayForMatch,
    stripMatchToUnscheduled,
    clearMatchExplicitEndTimes,
    isMatchScheduledOnGrid,
    isMatchScheduledInDatabase,
    sanitizeScheduledMatches,
} from '../../pages/tourDetails/Schedule/components/schedule.helpers';
import { wouldOverlap, dedupeScheduledMatchesById, flattenScheduledMatches } from './scheduleOverlap';
import { reconcileScheduleTimezoneOnSync } from './scheduleTimezone';
import {
    courtKeyForMatch,
    courtKeyFromSlot,
    patchScheduleFromFlatMatches,
    type CourtKey,
} from './scheduleTimelineUpdate';
import { createPlacementInFlightGate } from '../../pages/tourDetails/Schedule/components/DnDSchedule/schedulePlacementKeyboard';
import {
    type BulkAdjustOps,
    type BulkTransformResult,
    computeGroupDragOpsFromFreeSlotDrop,
    hasBulkOpsEffect,
    simulateBulkScheduleTransform,
} from './scheduleBulkTransform';
import { MIN_SCHEDULE_BULK_ADJUST } from './scheduleSelectionConstants';

/** Dev: tweak toast corner for schedule placement messages (`top-right`, `bottom-right`, …). */
export const SCHEDULE_ACTION_TOAST_POSITION: ToastPosition = 'bottom-right';

const swapScheduleInFlightGate = createPlacementInFlightGate();

const SCHEDULE_I18N_PREFIX = 'creativeDetail-';

const pushLocalScheduleHistory = (dispatch: AppDispatch, getState: () => RootState) => {
    const { schedule, unscheduledMatches } = selectSchedule(getState());
    dispatch(pushScheduleHistoryBeforeEdit(snapshotScheduleState(schedule, unscheduledMatches)));
};

const courtKeysForMoveOrSwap = (
    sourceItem: IMatchScheduleItem,
    targetItem: IMatchScheduleItem | IFreeSlotItem,
    constraints: IScheduleConstraints,
    timeZone: string,
    allMatches: IMatchScheduleItem[]
): CourtKey[] => {
    const keys: CourtKey[] = [];
    const sourceKey = courtKeyForMatch(sourceItem, constraints, timeZone, allMatches);
    if (sourceKey) keys.push(sourceKey);
    if (isFreeSlot(targetItem)) {
        keys.push(courtKeyFromSlot(targetItem.day, targetItem.court.id));
    } else {
        const targetKey = courtKeyForMatch(targetItem, constraints, timeZone, allMatches);
        if (targetKey) keys.push(targetKey);
    }
    return keys;
};

const notifySchedulePlacementInfo = (
    dispatch: AppDispatch,
    t: TFunction<'translation', undefined>,
    message: string
) => {
    scheduleActionInfoWithUndo(
        message,
        SCHEDULE_ACTION_TOAST_POSITION,
        () => {
            dispatch(undoScheduleChange());
        },
        t(SCHEDULE_I18N_PREFIX + 'scheduleUndoToastAction', 'Undo')
    );
};

const notifySchedulePlacementSuccess = (
    dispatch: AppDispatch,
    t: TFunction<'translation', undefined>,
    message: string
) => {
    scheduleActionSuccessWithUndo(
        message,
        SCHEDULE_ACTION_TOAST_POSITION,
        () => {
            dispatch(undoScheduleChange());
        },
        t(SCHEDULE_I18N_PREFIX + 'scheduleUndoToastAction', 'Undo')
    );
};

function commitBulkScheduleTransformFromPreview(
    dispatch: AppDispatch,
    getState: () => RootState,
    targetMatchIds: number[],
    preview: BulkTransformResult,
    t: TFunction<'translation', undefined>
): boolean {
    const prefix = 'creativeDetail-';
    const { scheduleConstraints, tournamentInfo } = selectSchedule(getState());

    const appliedCount = preview.stats.appliedCount;
    if (appliedCount <= 0) {
        Notification(
            TypeError.error,
            t(prefix + 'scheduleBulkAdjustNoneApplied', 'No matches could be adjusted.')
        );
        return false;
    }

    const timeZone = getTournamentTimezone(tournamentInfo?.country, tournamentInfo?.timeZone);
    const rebuiltSchedule =
        preview.rebuiltSchedule ??
        rebuildEntireSchedule(preview.scheduledMatches, scheduleConstraints!, timeZone);

    pushLocalScheduleHistory(dispatch, getState);
    dispatch(setScheduleState(rebuiltSchedule));
    dispatch(setUnscheduledMatchesState(preview.unscheduledMatches));
    dispatch(clearScheduleSelection());

    if (preview.stats.skippedCount > 0) {
        notifySchedulePlacementInfo(
            dispatch,
            t,
            t(prefix + 'scheduleBulkAdjustAppliedWithSkipped', {
                applied: appliedCount,
                skipped: preview.stats.skippedCount,
                defaultValue: 'Adjusted {{applied}} matches; {{skipped}} skipped.',
            })
        );
    } else {
        notifySchedulePlacementSuccess(
            dispatch,
            t,
            t(prefix + 'scheduleBulkAdjustApplied', {
                count: appliedCount,
                defaultValue: 'Adjusted {{count}} matches.',
            })
        );
    }
    return true;
}

export const fetchDataForSchedule =
    (tournamentId: number, force = false, preloadedInfo?: TournamentState) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
        const { tournamentInfo } = selectSchedule(getState());
        try {
            if (force || tournamentInfo === null || tournamentInfo.id !== tournamentId) {
                dispatch(restoreInitialState());
                dispatch(initialDataFetching());
                const info = preloadedInfo ?? (await fetchTournamentInfo(tournamentId));
                dispatch(setTournamentInfo(info));

                try {
                    const constraints = await getScheduleConstraints(info.id);

                    if (!constraints || !constraints.days) {
                        throw new Error("No constraints found in DB");
                    }

                    dispatch(setScheduleConstraints(constraints));
                    dispatch(setScheduleConstraintsGiven(true));
                } catch (e) {
                    if (info.startDate && info.endDate) {
                        const timeZone = getTournamentTimezone(info.country, info.timeZone);
                        const constraints = generateDefaultSchedulingConstraints(info, timeZone);
                        dispatch(setScheduleConstraints(constraints));
                    }
                }
                try {
                    const { schedule, unscheduled } = await getTournamentSchedule(info.id);
                    const { scheduleConstraints: loadedConstraints } = selectSchedule(getState());
                    const timeZone = getTournamentTimezone(info.country, info.timeZone);

                    if (unscheduled && unscheduled.length > 0) {
                        dispatch(setUnscheduledMatchesState(unscheduled));
                    }

                    if (schedule && schedule.days.length > 0 && loadedConstraints != null) {
                        const flatScheduled = schedule.days.flatMap((day) =>
                            day.courts.flatMap(
                                (court) =>
                                    court.matches.filter(
                                        (m): m is IMatchScheduleItem => !isFreeSlot(m)
                                    )
                            )
                        );
                        const sanitized = sanitizeScheduledMatches(flatScheduled, loadedConstraints);
                        const normalizedSchedule = rebuildEntireSchedule(
                            sanitized.scheduled,
                            loadedConstraints,
                            timeZone
                        );

                        const sanitizedUnscheduledIds = new Set(
                            sanitized.unscheduled.map((match) => match.id)
                        );
                        const nextUnscheduled = [
                            ...(unscheduled ?? []).filter(
                                (match) => !sanitizedUnscheduledIds.has(match.id)
                            ),
                            ...sanitized.unscheduled,
                        ];

                        dispatch(setUnscheduledMatchesState(nextUnscheduled));
                        dispatch(setScheduleStateFromSource(normalizedSchedule));
                        dispatch(setLoadedSavedSchedule());

                        if (sanitized.unscheduled.length > 0) {
                            try {
                                const persistPayload = buildSchedulePersistPayload(
                                    sanitized.scheduled,
                                    nextUnscheduled,
                                    loadedConstraints
                                );
                                await updateTournamentSchedule(
                                    info.id,
                                    loadedConstraints,
                                    persistPayload
                                );
                                notifyInvalidIntervalSanitization(sanitized.unscheduled.length);
                            } catch (persistError) {
                                console.error('Failed to persist invalid schedule interval fixes', persistError);
                            }
                        }
                    } else if (schedule && schedule.days.length > 0) {
                        dispatch(setScheduleStateFromSource(schedule));
                        dispatch(setLoadedSavedSchedule());
                    }
                } catch (error) {
                    console.log("No schedule generated yet.");
                }

                dispatch(initialDataFetchSuccess());
            }
        } catch (e) {
            const err = e as Error;
            dispatch(initialDataFetchError(err.message));
        }
    };

const fetchTournamentInfo = async (id: number) => {
    return await fetchTournamentWithClassAndCourtListByTournamentId(id);
};

export const pollScheduleStatus =
    (tournamentId: number, t: TFunction<'translation', undefined>) =>
        async (dispatch: AppDispatch, getState: () => RootState) => {
            const prefix = 'creativeDetail-';
            const MAX_POLL_ATTEMPTS = 120; // 5 minutes max at 5s intervals
            let attempts = 0;

            const checkQueueStatus = async (intervalId?: NodeJS.Timeout) => {
                if (!getState().schedule.isGeneratingAsync) {
                    if (intervalId) clearInterval(intervalId);
                    return;
                }

                attempts++;
                try {
                    const {
                        status,
                        queuePosition,
                        etaSeconds,
                        errorMessage
                    } = await getScheduleJobStatus(tournamentId);

                    if (status === 'completed') {
                        if (intervalId) clearInterval(intervalId);

                        try {
                            const { schedule, unscheduled } = await getTournamentSchedule(tournamentId);
                            const { scheduleConstraints: loadedConstraints, tournamentInfo: info } =
                                selectSchedule(getState());
                            if (schedule && schedule.days.length > 0) {
                                const timeZone = getTournamentTimezone(
                                    info?.country,
                                    info?.timeZone
                                );
                                const normalizedSchedule =
                                    loadedConstraints != null
                                        ? normalizeScheduleWithConstraints(
                                              schedule,
                                              loadedConstraints,
                                              timeZone
                                          )
                                        : schedule;
                                dispatch(setScheduleStateFromSource(normalizedSchedule));
                                dispatch(setUnscheduledMatchesState(unscheduled));
                                dispatch(setLoadedSavedSchedule());
                            }
                        } catch (error) {
                            console.error("Failed to fetch generated schedule");
                        }

                        dispatch(setGeneratingAsync(false));
                        Notification(TypeError.success, t(prefix + 'scheduleAllMatchesSuccess'));
                    }
                    else if (status === 'failed') {
                        if (intervalId) clearInterval(intervalId);
                        dispatch(setGeneratingAsync(false));

                        const errorText: string = errorMessage || t(prefix + 'scheduleGenerationFailed', "Schedule generation failed. Constraints may be too tight.");

                        dispatch(creatingScheduleError(errorText));
                        dispatch(clearScheduleStates());
                    }
                    else if (status === 'cancelled') {
                        if (intervalId) clearInterval(intervalId);
                        dispatch(setGeneratingAsync(false));
                        dispatch(creatingScheduleError(t(prefix + 'scheduleGenerationCancelled', "Schedule generation was cancelled.")));
                    }
                    else if (status === 'queued' || status === 'processing' || status === 'cancelling') {
                        dispatch(setJobProgress({ queuePosition, etaSeconds }));
                    }
                    else if (attempts >= MAX_POLL_ATTEMPTS) {
                        if (intervalId) clearInterval(intervalId);
                        dispatch(setGeneratingAsync(false));
                        dispatch(creatingScheduleError(t(prefix + 'scheduleGenerationTimeout', "Schedule generation timed out.")));
                    }
                } catch (error) {
                    if (intervalId) clearInterval(intervalId);
                    dispatch(setGeneratingAsync(false));
                    dispatch(creatingScheduleError(t(prefix + 'scheduleGenerationStatusError', "Error checking schedule status.")));
                }
            };

            await checkQueueStatus();

            const pollInterval = setInterval(() => {
                checkQueueStatus(pollInterval);
            }, 2500);
        };



export const createScheduleAction =
    (t: TFunction<'translation', undefined>) =>
        async (dispatch: AppDispatch, getState: () => RootState) => {
            dispatch(creatingSchedule());

            try {
                const { scheduleConstraints, tournamentInfo } = selectSchedule(getState());
                if (!!tournamentInfo && !!scheduleConstraints) {

                    dispatch(setGeneratingAsync(true));
                    await createTournamentSchedule(
                        tournamentInfo.id,
                        scheduleConstraints
                    );

                    dispatch(creatingScheduleSuccess());
                    dispatch(pollScheduleStatus(tournamentInfo.id, t));

                } else {
                    throw new Error('Must fill in tournament info and schedule constraints');
                }
            } catch (e) {
                const err = e as Error;
                dispatch(creatingScheduleError(err.message));
                dispatch(setGeneratingAsync(false)); // Reset async state on error
            }
        };

export const removeSchedule =
    (t: TFunction<'translation', undefined>) =>
        async (dispatch: AppDispatch, getState: () => RootState) => {
            const prefix = 'creativeDetail-';

            const { tournamentInfo } = selectSchedule(getState());
            const id = tournamentInfo!.id;

            try {
                await removeTournamentSchedule(id);
                dispatch(restoreInitialState());
                await dispatch(fetchDataForSchedule(id));
                Notification(TypeError.info, t(prefix + 'scheduleRemovedInfo'));
                return true;
            } catch (e) {
                const err = e as Error;
                Notification(TypeError.error, err.message);
                return false;
            }
        };

const isServerBackedScheduleDto = (match: IMatchScheduleItemDto): boolean => {
    return Number.isInteger(match.id) && match.id > 0;
};

/** Resolves planned slot end for API payloads (prefers scheduleEndDate, then legacy endDate, then duration). */
const scheduledSlotEndDate = (
    m: IMatchScheduleItem,
    constraints: IScheduleConstraints
): Date => {
    const t0 = m.scheduleDate || m.startDate;
    const startMs = new Date(t0!).getTime();
    if (m.scheduleEndDate) {
        const endMs = new Date(m.scheduleEndDate).getTime();
        if (Number.isFinite(endMs) && endMs > startMs) {
            return new Date(m.scheduleEndDate);
        }
    }
    if (m.endDate) {
        const legacyEndMs = new Date(m.endDate).getTime();
        if (Number.isFinite(legacyEndMs) && legacyEndMs > startMs) {
            return new Date(m.endDate);
        }
    }
    const duration = m.duration || getMatchDurationFromConstraints(m, constraints) || 30;
    return new Date(startMs + duration * 60000);
};

const buildSchedulePersistPayload = (
    scheduledMatches: IMatchScheduleItem[],
    unscheduledMatches: IMatchScheduleItem[],
    constraints: IScheduleConstraints
): IMatchScheduleItemDto[] => {
    const scheduledPayload = scheduledMatches.filter(isMatchScheduledInDatabase).map((m) => {
        const t0 = m.scheduleDate || m.startDate;
        const courtId = m.court?.id ?? (m as { courtId?: number | null }).courtId;
        if (courtId == null) {
            throw new Error(`Scheduled match ${m.id} is missing courtId`);
        }
        return {
            id: m.id,
            scheduleDate: new Date(t0!),
            startDate: new Date(t0!),
            scheduleEndDate: scheduledSlotEndDate(m, constraints),
            court: m.court ?? ({ id: courtId } as Court),
            courtId,
        };
    });
    const unscheduledPayload = unscheduledMatches.map((m) => ({
        id: m.id,
        scheduleDate: null,
        startDate: null,
        scheduleEndDate: null,
        court: null,
        courtId: null,
    }));
    return [...scheduledPayload, ...unscheduledPayload].filter(isServerBackedScheduleDto);
};

const flattenScheduledMatchesFromApiSchedule = (
    schedule: ITournamentSchedule | null | undefined
): IMatchScheduleItem[] => {
    if (!schedule?.days?.length) return [];
    return schedule.days.flatMap((day) =>
        day.courts.flatMap((court) =>
            court.matches.filter((m): m is IMatchScheduleItem => !isFreeSlot(m))
        )
    );
};

/** Grid + API "unscheduled" rows that still have court/time in DB (renderer could not place them). */
const collectScheduledMatchesForTimezoneSyncPersist = (
    schedule: ITournamentSchedule | null | undefined,
    apiUnscheduled: IMatchScheduleItem[] | undefined
): IMatchScheduleItem[] => {
    const byId = new Map<number, IMatchScheduleItem>();
    for (const match of [
        ...flattenScheduledMatchesFromApiSchedule(schedule),
        ...(apiUnscheduled ?? []).filter(isMatchScheduledInDatabase),
    ]) {
        byId.set(match.id, match);
    }
    return [...byId.values()];
};

const mergeUnscheduledForPersist = (
    apiUnscheduled: IMatchScheduleItem[] | undefined,
    reduxUnscheduled: IMatchScheduleItem[],
    scheduledIds: ReadonlySet<number>
): IMatchScheduleItem[] => {
    const byId = new Map<number, IMatchScheduleItem>();
    for (const match of [...(apiUnscheduled ?? []), ...reduxUnscheduled]) {
        if (scheduledIds.has(match.id)) continue;
        byId.set(match.id, match);
    }
    return [...byId.values()];
};

const notifyInvalidIntervalSanitization = (unscheduledCount: number) => {
    if (unscheduledCount <= 0) return;
    Notification(
        TypeError.info,
        i18n.t(SCHEDULE_I18N_PREFIX + 'scheduleInvalidIntervalUnscheduleInfo', {
            count: unscheduledCount,
        }),
        SCHEDULE_ACTION_TOAST_POSITION
    );
};

export const updateTournamentScheduleAction =
    (schedule: ITournamentSchedule, t: TFunction<'translation', undefined>) =>
        async (dispatch: AppDispatch, getState: () => RootState) => {
            dispatch(updateSchedule());
            const { tournamentInfo, scheduleConstraints, unscheduledMatches } = selectSchedule(
                getState()
            );

            // Get matches currently on schedule
            const scheduledPayload = schedule.days.flatMap((day) => {
                return day.courts
                    .flatMap((courtSchedule) => courtSchedule.matches)
                    .filter((match) => !isFreeSlot(match) && isScheduled(match))
                    .map((match) => {
                        const m = match as IMatchScheduleItem;
                        return {
                            id: m.id,
                            scheduleDate: new Date(m.scheduleDate!),
                            startDate: new Date(m.scheduleDate!),
                            scheduleEndDate: scheduledSlotEndDate(m, scheduleConstraints!),
                            court: m.court,
                            courtId: m.court.id,
                        };
                    });
            });

            // Get matches off schedule (unscheduled)
            const unscheduledPayload = unscheduledMatches.map((m) => ({
                id: m.id,
                scheduleDate: null,
                startDate: null,
                scheduleEndDate: null,
                court: null,
                courtId: null,
            }));

            // Combine them and keep client-only temporary rows in Redux only.
            const matches: IMatchScheduleItemDto[] = [...scheduledPayload, ...unscheduledPayload]
                .filter(isServerBackedScheduleDto);

            try {
                await updateTournamentSchedule(tournamentInfo!.id, scheduleConstraints!, matches);
                dispatch(setLoadedSavedSchedule());
                dispatch(updateScheduleSuccess());
                return true;
            } catch (e) {
                const err = e as Error;
                dispatch(updateScheduleError(err.message));
                return false;
            }
        };

export const remediateScheduleTimezoneAction =
    (t: TFunction<'translation', undefined>, _communityId: number) =>
        async (dispatch: AppDispatch, getState: () => RootState) => {
            const { tournamentInfo, scheduleConstraints, unscheduledMatches } =
                selectSchedule(getState());

            if (
                !tournamentInfo?.id ||
                !tournamentInfo.timeZone ||
                !scheduleConstraints?.scheduleTimezone
            ) {
                dispatch(updateScheduleError('Missing tournament timezone or schedule constraints'));
                return false;
            }

            dispatch(updateSchedule());

            let constraintsForReconcile = scheduleConstraints;
            try {
                constraintsForReconcile =
                    (await getScheduleConstraints(tournamentInfo.id)) ?? scheduleConstraints;
            } catch {
                // Fall back to in-memory constraints when the API is unavailable.
            }

            const staleTimeZone =
                constraintsForReconcile.scheduleTimezone ?? scheduleConstraints.scheduleTimezone;
            if (!staleTimeZone) {
                dispatch(updateScheduleError('Missing tournament timezone or schedule constraints'));
                return false;
            }

            const reconciledConstraints = reconcileScheduleTimezoneOnSync(
                constraintsForReconcile,
                staleTimeZone,
                tournamentInfo.timeZone,
                {
                    startDate: tournamentInfo.startDate,
                    endDate: tournamentInfo.endDate,
                }
            );

            const { schedule: apiSchedule, unscheduled: apiUnscheduled } =
                await getTournamentSchedule(tournamentInfo.id);
            const apiScheduledMatches = collectScheduledMatchesForTimezoneSyncPersist(
                apiSchedule,
                apiUnscheduled
            );
            const scheduledIds = new Set(apiScheduledMatches.map((m) => m.id));
            const mergedUnscheduled = mergeUnscheduledForPersist(
                apiUnscheduled,
                unscheduledMatches,
                scheduledIds
            );
            const matches = buildSchedulePersistPayload(
                apiScheduledMatches,
                mergedUnscheduled,
                reconciledConstraints
            );

            try {
                await updateTournamentSchedule(tournamentInfo.id, reconciledConstraints, matches);
                dispatch(setScheduleConstraints(reconciledConstraints));
                await dispatch(fetchDataForSchedule(tournamentInfo.id, true));
                dispatch(updateScheduleSuccess());
                Notification(TypeError.success, t('creativeDetail-scheduleTimezoneSyncSuccess'), 'bottom-right');
                return true;
            } catch (e) {
                const err = e as Error;
                dispatch(updateScheduleError(err.message));
                Notification(TypeError.error, t('creativeDetail-scheduleTimezoneSyncError'), 'bottom-right');
                return false;
            }
        };

export type PartialUnscheduleFromLayoutPayload =
    | {
        mode: 'byClass';
        classIds: number[];
        dayScope: 'all' | 'selected';
        dayKeys: number[];
        includeStartedAndFinished: boolean;
    }
    | {
        mode: 'byDay';
        dayKeys: number[];
        includeStartedAndFinished: boolean;
    };

type ScheduledMatchWithDay = {
    dayKey: number;
    match: IMatchScheduleItem;
};

const getLayoutMatchKey = (match: IMatchScheduleItem): string => {
    return Number.isInteger(match.id) && match.id > 0
        ? `id:${match.id}`
        : `client:${match.matchId || match.id}`;
};

const isNotStartedScheduleMatch = (match: IMatchScheduleItem): boolean => {
    return Number(match.status) === TournamentMatchStatusEnum.NOT_STARTED;
};

export const partialUnscheduleFromLayoutAction =
    (payload: PartialUnscheduleFromLayoutPayload, t: TFunction<'translation', undefined>) =>
        async (dispatch: AppDispatch, getState: () => RootState) => {
            const prefix = 'creativeDetail-';
            const {
                schedule,
                scheduleConstraints,
                unscheduledMatches,
                tournamentInfo,
            } = selectSchedule(getState());

            if (!schedule || !scheduleConstraints || !tournamentInfo) {
                Notification(TypeError.error, t(prefix + 'scheduleSettingsMissingData'));
                return false;
            }

            const selectedClassIds = payload.mode === 'byClass'
                ? new Set(payload.classIds)
                : new Set<number>();
            const selectedDayKeys = new Set(payload.dayKeys);

            const scheduledMatches: ScheduledMatchWithDay[] = schedule.days.flatMap((day) =>
                day.courts.flatMap((court) =>
                    court.matches
                        .filter((match): match is IMatchScheduleItem => !isFreeSlot(match))
                        .map((match) => ({ dayKey: day.dayKey, match }))
                )
            );

            const candidates = scheduledMatches.filter(({ dayKey, match }) => {
                if (payload.mode === 'byDay') {
                    return selectedDayKeys.has(dayKey);
                }

                const dayMatchesScope =
                    payload.dayScope === 'all' || selectedDayKeys.has(dayKey);
                return selectedClassIds.has(Number(match.classId)) && dayMatchesScope;
            });

            const matchesToRemove = payload.includeStartedAndFinished
                ? candidates
                : candidates.filter(({ match }) => isNotStartedScheduleMatch(match));
            const skippedCount = candidates.length - matchesToRemove.length;

            if (skippedCount > 0) {
                Notification(
                    TypeError.info,
                    t(prefix + 'scheduleSettingsSkippedStartedMatches', { count: skippedCount })
                );
            }

            if (matchesToRemove.length === 0) {
                Notification(TypeError.info, t(prefix + 'scheduleSettingsNoEligibleMatches'));
                return false;
            }

            const removedKeys = new Set(matchesToRemove.map(({ match }) => getLayoutMatchKey(match)));
            const keptMatches = scheduledMatches
                .filter(({ match }) => !removedKeys.has(getLayoutMatchKey(match)))
                .map(({ match }) => match);

            const unscheduledByKey = new Map<string, IMatchScheduleItem>();
            unscheduledMatches.forEach((match) => {
                unscheduledByKey.set(getLayoutMatchKey(match), match);
            });
            matchesToRemove.forEach(({ match }) => {
                unscheduledByKey.set(getLayoutMatchKey(match), stripMatchToUnscheduled(match));
            });

            const timeZone = getTournamentTimezone(tournamentInfo.country, tournamentInfo.timeZone);
            const rebuiltSchedule = rebuildEntireSchedule(keptMatches, scheduleConstraints, timeZone);
            const nextUnscheduledMatches = Array.from(unscheduledByKey.values());
            const removedServerBackedCount = matchesToRemove.filter(({ match }) =>
                Number.isInteger(match.id) && match.id > 0
            ).length;

            const { schedule: previousSchedule, unscheduledMatches: previousUnscheduled } =
                selectSchedule(getState());

            pushLocalScheduleHistory(dispatch, getState);
            dispatch(applyScheduleAndUnscheduled({
                schedule: rebuiltSchedule,
                unscheduledMatches: nextUnscheduledMatches,
            }));

            if (removedServerBackedCount === 0) {
                Notification(TypeError.info, t(prefix + 'scheduleSettingsClientOnlyInfo'));
                return true;
            }

            const updateSucceeded = await dispatch(updateTournamentScheduleAction(rebuiltSchedule, t));
            if (!updateSucceeded) {
                if (previousSchedule) {
                    dispatch(applyScheduleAndUnscheduled({
                        schedule: previousSchedule,
                        unscheduledMatches: previousUnscheduled,
                    }));
                }
                dispatch(discardLastScheduleUndoSnapshot());
                Notification(TypeError.error, t(prefix + 'scheduleSettingsUpdateFailed'));
                return false;
            }

            Notification(TypeError.success, t(prefix + 'scheduleSettingsPartialSuccess'));
            return true;
        };

export const publishTournamentScheduleAction =
    (schedule: ITournamentSchedule, t: TFunction<'translation', undefined>) =>
        async (dispatch: AppDispatch, getState: () => RootState) => {
            dispatch(publishSchedule());
            const { tournamentInfo, scheduleConstraints, unscheduledMatches } = selectSchedule(
                getState()
            );

            // Get matches currently on schedule
            const scheduledPayload = schedule.days.flatMap((day) => {
                return day.courts
                    .flatMap((courtSchedule) => courtSchedule.matches)
                    .filter((match) => !isFreeSlot(match) && isScheduled(match))
                    .map((match) => {
                        const m = match as IMatchScheduleItem;
                        return {
                            id: m.id,
                            scheduleDate: new Date(m.scheduleDate!),
                            startDate: new Date(m.scheduleDate!),
                            scheduleEndDate: scheduledSlotEndDate(m, scheduleConstraints!),
                            court: m.court,
                            courtId: m.court.id,
                        };
                    });
            });

            // Get matches off the schedule (unscheduled)
            const unscheduledPayload = unscheduledMatches.map((m) => ({
                id: m.id,
                scheduleDate: null,
                startDate: null,
                scheduleEndDate: null,
                court: null,
                courtId: null,
            }));

            // Combine them and keep client-only temporary rows in Redux only.
            const matches: IMatchScheduleItemDto[] = [...scheduledPayload, ...unscheduledPayload]
                .filter(isServerBackedScheduleDto);

            try {
                const { schedule, unscheduled } = await publishTournamentSchedule(
                    tournamentInfo!.id,
                    scheduleConstraints!,
                    matches
                );
                dispatch(setScheduleStateFromSource(schedule));
                dispatch(publishScheduleSuccess());
                dispatch(setUnscheduledMatchesState(unscheduled));

                dispatch(setLoadedSavedSchedule());
            } catch (e) {
                const err = e as Error;
                dispatch(publishScheduleError(err.message));
            }
        };

export const swapScheduleItems =
    (
        sourceItem: IMatchScheduleItem | IFreeSlotItem,
        targetItem: IMatchScheduleItem | IFreeSlotItem,
        t: TFunction<'translation', undefined>
    ) =>
        async (dispatch: AppDispatch, getState: () => RootState): Promise<boolean> => {
            if (!swapScheduleInFlightGate.tryAcquire()) {
                return false;
            }
            try {
            const prefix = 'creativeDetail-';
            const { schedule, scheduleConstraints, tournamentInfo } = selectSchedule(getState());

            if (isFreeSlot(sourceItem)) return false; // Don't drag free slots

            let allMatches = schedule!.days.flatMap(d =>
                d.courts.flatMap(c => c.matches.filter(m => !isFreeSlot(m)) as IMatchScheduleItem[])
            );

            const sourceDuration = sourceItem.duration || getMatchDurationFromConstraints(sourceItem as IMatchScheduleItem, scheduleConstraints!) || 30;
            const targetStartMs = new Date(targetItem.scheduleDate!).getTime();
            const targetId = isFreeSlot(targetItem) ? -1 : targetItem.id;

            if (wouldOverlap(targetStartMs, sourceDuration, targetItem.court.id, [sourceItem.id, targetId], allMatches, scheduleConstraints!)) {
                Notification(TypeError.error, t(prefix + 'overlapError', 'Not enough time available in this slot.'));
                return false;
            }

            if (!isFreeSlot(targetItem)) {
                const targetDuration = targetItem.duration || getMatchDurationFromConstraints(targetItem as IMatchScheduleItem, scheduleConstraints!) || 30;
                const sourceStartMs = new Date(sourceItem.scheduleDate!).getTime();

                if (wouldOverlap(sourceStartMs, targetDuration, sourceItem.court.id, [sourceItem.id, targetItem.id], allMatches, scheduleConstraints!)) {
                    Notification(TypeError.error, t(prefix + 'overlapError', 'Cannot swap. Not enough time available at the original slot.'));
                    return false;
                }
            }

            const matchIndex = allMatches.findIndex(m => m.id === sourceItem.id);
            if (matchIndex === -1) return false;

            allMatches[matchIndex] = {
                ...allMatches[matchIndex],
                scheduleDate: targetItem.scheduleDate,
                startDate: targetItem.scheduleDate!,
                ...clearMatchExplicitEndTimes(allMatches[matchIndex]),
                court: targetItem.court
            };

            if (!isFreeSlot(targetItem)) {
                const targetMatchIndex = allMatches.findIndex(m => m.id === targetItem.id);
                if (targetMatchIndex !== -1) {
                    allMatches[targetMatchIndex] = {
                        ...allMatches[targetMatchIndex],
                        scheduleDate: sourceItem.scheduleDate,
                        startDate: sourceItem.scheduleDate!,
                        ...clearMatchExplicitEndTimes(allMatches[targetMatchIndex]),
                        court: sourceItem.court
                    };
                }
            }

            const timeZone = getTournamentTimezone(tournamentInfo?.country, tournamentInfo?.timeZone);
            const patchedSchedule = patchScheduleFromFlatMatches(
                schedule!,
                allMatches,
                scheduleConstraints!,
                timeZone,
                courtKeysForMoveOrSwap(sourceItem, targetItem, scheduleConstraints!, timeZone, allMatches)
            );

            pushLocalScheduleHistory(dispatch, getState);
            dispatch(setScheduleState(patchedSchedule));
            dispatch(setSelectedItem(null));
            dispatch(clearScheduleSelection());
            notifySchedulePlacementInfo(dispatch, t, swapMessage(sourceItem, targetItem, t));
            return true;
            } finally {
                swapScheduleInFlightGate.release();
            }
        };

export const addUnscheduledItemToSchedule =
    (
        freeSlot: IFreeSlotItem,
        unscheduledItem: IMatchScheduleItem,
        t: TFunction<'translation', undefined>
    ) =>
        async (dispatch: AppDispatch, getState: () => RootState): Promise<boolean> => {
            const prefix = 'creativeDetail-';
            const { schedule, scheduleConstraints, unscheduledMatches, tournamentInfo } = selectSchedule(getState());

            let allMatches = schedule!.days.flatMap(d =>
                d.courts.flatMap(c => c.matches.filter(m => !isFreeSlot(m)) as IMatchScheduleItem[])
            );

            const duration = unscheduledItem.duration || getMatchDurationFromConstraints(unscheduledItem, scheduleConstraints!) || 30;
            const proposedStartMs = new Date(freeSlot.scheduleDate).getTime();

            if (wouldOverlap(proposedStartMs, duration, freeSlot.court.id, [], allMatches, scheduleConstraints!)) {
                Notification(TypeError.error, t(prefix + 'overlapError', 'Not enough time available in this slot.'));
                return false;
            }

            const newlyScheduledMatch = {
                ...unscheduledItem,
                scheduleDate: freeSlot.scheduleDate,
                startDate: freeSlot.scheduleDate,
                court: freeSlot.court,
                duration
            };
            allMatches.push(newlyScheduledMatch);

            const timeZone = getTournamentTimezone(tournamentInfo?.country, tournamentInfo?.timeZone);
            const patchedSchedule = patchScheduleFromFlatMatches(
                schedule!,
                allMatches,
                scheduleConstraints!,
                timeZone,
                [courtKeyFromSlot(freeSlot.day, freeSlot.court.id)]
            );

            pushLocalScheduleHistory(dispatch, getState);
            dispatch(setUnscheduledMatchesState(
                unscheduledMatches.filter(({ id }) => id !== unscheduledItem.id)
            ));
            dispatch(setScheduleState(patchedSchedule));
            dispatch(setSelectedItem(null));
            dispatch(clearScheduleSelection());
            notifySchedulePlacementInfo(dispatch, t, swapMessage(unscheduledItem, freeSlot, t));
            return true;
        };

/** Place pending unscheduled match at a scheduled match's slot and move that match to unscheduled (click mode + Ctrl/Cmd). */
export const swapPendingUnscheduledWithScheduled =
    (
        pendingMatch: IMatchScheduleItem,
        targetScheduledMatch: IMatchScheduleItem,
        t: TFunction<'translation', undefined>
    ) =>
        async (dispatch: AppDispatch, getState: () => RootState): Promise<boolean> => {
            const prefix = 'creativeDetail-';
            const { schedule, scheduleConstraints, unscheduledMatches, tournamentInfo } = selectSchedule(getState());

            if (!schedule || !scheduleConstraints) return false;
            if (!targetScheduledMatch.scheduleDate || !targetScheduledMatch.court) return false;
            if (pendingMatch.id === targetScheduledMatch.id) return false;

            if (
                !unscheduledMatches.some(
                    (m) => getLayoutMatchKey(m) === getLayoutMatchKey(pendingMatch)
                )
            ) {
                return false;
            }

            let allMatches = schedule.days.flatMap((d) =>
                d.courts.flatMap((c) => c.matches.filter((m) => !isFreeSlot(m)) as IMatchScheduleItem[])
            );

            const targetIdx = allMatches.findIndex((m) => m.id === targetScheduledMatch.id);
            if (targetIdx === -1) return false;

            const target = allMatches[targetIdx];
            const pendingDuration =
                pendingMatch.duration ||
                getMatchDurationFromConstraints(pendingMatch, scheduleConstraints) ||
                30;
            const proposedStartMs = new Date(target.scheduleDate!).getTime();

            if (
                wouldOverlap(
                    proposedStartMs,
                    pendingDuration,
                    target.court.id,
                    [target.id],
                    allMatches,
                    scheduleConstraints
                )
            ) {
                Notification(
                    TypeError.error,
                    t(prefix + 'overlapError', 'Not enough time available in this slot.')
                );
                return false;
            }

            const displaced = { ...target };
            const withoutTarget = allMatches.filter((m) => m.id !== target.id);

            const newlyScheduled = {
                ...pendingMatch,
                scheduleDate: target.scheduleDate,
                startDate: target.scheduleDate!,
                ...clearMatchExplicitEndTimes(pendingMatch),
                court: target.court,
                duration: pendingDuration,
            };
            withoutTarget.push(newlyScheduled);

            const matchForUnscheduled = {
                ...displaced,
                scheduleDate: null,
                startDate: null,
                ...clearMatchExplicitEndTimes(displaced),
                court: null,
            } as unknown as IMatchScheduleItem;

            const timeZone = getTournamentTimezone(tournamentInfo?.country, tournamentInfo?.timeZone);
            const targetDay = resolveTournamentDayForMatch(target, scheduleConstraints, timeZone, [
                ...withoutTarget,
                newlyScheduled,
            ]);
            const targetCourtKey = targetDay
                ? courtKeyFromSlot(targetDay.dayKey, target.court.id)
                : courtKeyForMatch(target, scheduleConstraints, timeZone, withoutTarget);
            const patchedSchedule = patchScheduleFromFlatMatches(
                schedule,
                withoutTarget,
                scheduleConstraints,
                timeZone,
                targetCourtKey ? [targetCourtKey] : []
            );

            const nextUnscheduled = [
                ...unscheduledMatches.filter((m) => m.id !== pendingMatch.id),
                matchForUnscheduled,
            ];

            pushLocalScheduleHistory(dispatch, getState);
            dispatch(setScheduleState(patchedSchedule));
            dispatch(setUnscheduledMatchesState(nextUnscheduled));
            dispatch(setSelectedItem(null));
            dispatch(clearScheduleSelection());
            notifySchedulePlacementInfo(
                dispatch,
                t,
                t(
                    prefix + 'schedulePlacementCtrlSwapSuccess',
                    'Unscheduled match placed here; the replaced match was moved to unscheduled.'
                )
            );
            return true;
        };

const swapMessage = (
    item: IMatchScheduleItem | IFreeSlotItem,
    storedItem: IMatchScheduleItem | IFreeSlotItem,
    t: TFunction<'translation', undefined>
) => {
    const getMatchIdentifier = (m: IMatchScheduleItem) => {
        return m.matchId ? `Match ${m.matchId}` : `Match ${m.id}`;
    };

    if (!isFreeSlot(item) && !isFreeSlot(storedItem)) {
        return (
            getMatchIdentifier(storedItem) +
            ' ' +
            t('creativeDetail-itemMovedTo', 'moved to') +
            ' ' +
            item.court.name +
            ', ' +
            getMatchIdentifier(item) +
            ' ' +
            t('creativeDetail-itemMovedTo', 'moved to') +
            ' ' +
            storedItem.court.name
        );
    } else {
        return !isFreeSlot(item)
            ? getMatchIdentifier(item) + ' ' + t('creativeDetail-itemMovedTo', 'moved to') + ' ' + storedItem.court.name
            : getMatchIdentifier(storedItem as IMatchScheduleItem) +
            ' ' +
            t('creativeDetail-itemMovedTo', 'moved to') +
            ' ' +
            item.court.name;
    }
};

export const updateMatchDetailsAction =
    (
        matchId: number,
        newCourtId: number | null,
        newStartTimeIso: string | null,
        newDuration: number,
        t: TFunction<'translation', undefined>
    ) =>
        async (dispatch: AppDispatch, getState: () => RootState): Promise<boolean> => {
            const prefix = 'creativeDetail-';
            const { schedule, scheduleConstraints, tournamentInfo, unscheduledMatches } = selectSchedule(getState());

            let allMatches = schedule!.days.flatMap(d =>
                d.courts.flatMap(c => c.matches.filter(m => !isFreeSlot(m)) as IMatchScheduleItem[])
            );

            let matchIndex = allMatches.findIndex(m => m.id === matchId);
            let isFromUnscheduled = false;
            let matchToUpdate: IMatchScheduleItem;

            // Check if the match is in the schedule, otherwise check if it's unscheduled
            if (matchIndex !== -1) {
                matchToUpdate = allMatches[matchIndex];
            } else {
                const unschIndex = unscheduledMatches.findIndex(m => m.id === matchId);
                if (unschIndex !== -1) {
                    isFromUnscheduled = true;
                    matchToUpdate = unscheduledMatches[unschIndex];
                } else {
                    return false; // Match not found anywhere
                }
            }

            const timeZone = getTournamentTimezone(tournamentInfo?.country, tournamentInfo?.timeZone);

            // Case 1: Updating duration (no time)
            if (!newStartTimeIso) {
                if (isFromUnscheduled) {
                    const updatedUnscheduled = unscheduledMatches.map(m =>
                        m.id === matchId ? { ...m, duration: newDuration } : m
                    );
                    pushLocalScheduleHistory(dispatch, getState);
                    dispatch(setUnscheduledMatchesState(updatedUnscheduled));
                    notifySchedulePlacementSuccess(
                        dispatch,
                        t,
                        t(prefix + 'matchUpdated', 'Match duration updated.')
                    );
                } else {
                    const currentStartMs = new Date(matchToUpdate.scheduleDate!).getTime();
                    if (wouldOverlap(currentStartMs, newDuration, matchToUpdate.court.id, [matchId], allMatches, scheduleConstraints!)) {
                        Notification(TypeError.error, t(prefix + 'overlapError', 'Not enough time available to extend this match.'));
                        return false;
                    }
                    allMatches[matchIndex] = { ...matchToUpdate, duration: newDuration };
                    const durationCourtKey = courtKeyForMatch(
                        matchToUpdate,
                        scheduleConstraints!,
                        timeZone,
                        allMatches
                    );
                    const patchedSchedule = patchScheduleFromFlatMatches(
                        schedule!,
                        allMatches,
                        scheduleConstraints!,
                        timeZone,
                        durationCourtKey ? [durationCourtKey] : []
                    );
                    pushLocalScheduleHistory(dispatch, getState);
                    dispatch(setScheduleState(patchedSchedule));
                    notifySchedulePlacementSuccess(
                        dispatch,
                        t,
                        t(prefix + 'matchUpdated', 'Match duration updated.')
                    );
                }
                return true;
            }

            // Case 2: Scheduling or moving the match to a new time
            if (!newCourtId) return false;

            if (isFromUnscheduled) {
                matchIndex = allMatches.length;
                allMatches.push(matchToUpdate);
            }

            const proposedStartMs = new Date(newStartTimeIso).getTime();

            if (wouldOverlap(proposedStartMs, newDuration, newCourtId, [matchId], allMatches, scheduleConstraints!)) {
                Notification(TypeError.error, t(prefix + 'overlapError', 'Not enough time available on this court at this time.'));
                return false;
            }

            const newCourt = tournamentInfo!.courts.find(c => c.id === newCourtId);
            if (!newCourt) return false;

            const oldCourtKey =
                !isFromUnscheduled && matchToUpdate.scheduleDate
                    ? courtKeyForMatch(matchToUpdate, scheduleConstraints!, timeZone, allMatches)
                    : null;

            // Apply edits
            allMatches[matchIndex] = {
                ...matchToUpdate,
                scheduleDate: newStartTimeIso,
                startDate: newStartTimeIso,
                duration: newDuration,
                court: newCourt,
                ...clearMatchExplicitEndTimes(matchToUpdate),
            };

            const newCourtKey = courtKeyForMatch(
                allMatches[matchIndex],
                scheduleConstraints!,
                timeZone,
                allMatches
            );
            const moveCourtKeys: CourtKey[] = [];
            if (oldCourtKey) moveCourtKeys.push(oldCourtKey);
            if (newCourtKey) moveCourtKeys.push(newCourtKey);

            const patchedSchedule = patchScheduleFromFlatMatches(
                schedule!,
                allMatches,
                scheduleConstraints!,
                timeZone,
                moveCourtKeys
            );

            // Remove from unscheduled array if it was just scheduled
            pushLocalScheduleHistory(dispatch, getState);
            if (isFromUnscheduled) {
                dispatch(setUnscheduledMatchesState(unscheduledMatches.filter(m => m.id !== matchId)));
            }

            dispatch(setScheduleState(patchedSchedule));

            notifySchedulePlacementSuccess(
                dispatch,
                t,
                t(prefix + 'matchUpdated', 'Match scheduled successfully.')
            );

            return true;
        };

export const cancelScheduleAction =
    (t: TFunction<'translation', undefined>) =>
        async (dispatch: AppDispatch, getState: () => RootState) => {
            const prefix = 'creativeDetail-';
            const { tournamentInfo } = selectSchedule(getState());

            if (!tournamentInfo) return;

            dispatch(clearScheduleStates());
            dispatch(setGeneratingAsync(false));
            dispatch(creatingScheduleError('Schedule generation cancelled.'));

            try {
                await cancelTournamentScheduleGeneration(tournamentInfo.id);
                //Notification(TypeError.info, t(prefix + 'cancelSuccess', 'Schedule generation cancelled.'));
            } catch (e) {
                console.error("Failed to cancel on backend:", e);
                Notification(TypeError.error, t(prefix + 'cancelError', 'Failed to cancel schedule on server.'));
            }
        };

export const skipToManualScheduleAction =
    () =>
        async (dispatch: AppDispatch, getState: () => RootState) => {
            const { scheduleConstraints, schedule, unscheduledMatches, tournamentInfo } = selectSchedule(getState());

            if (!scheduleConstraints || !tournamentInfo) return;

            dispatch(creatingSchedule());

            // Get matches currently on grid
            const existingMatches = schedule?.days?.flatMap(day =>
                day.courts.flatMap(court =>
                    court.matches.filter(m => !isFreeSlot(m)) as IMatchScheduleItem[]
                )
            ) || [];

            // Align with API isScheduled: (scheduleDate || startDate) && court — not scheduleDate alone
            const isHandScheduled = (m: IMatchScheduleItem) => {
                const at = m.scheduleDate || m.startDate;
                return !!at && m.court != null;
            };

            const previouslyScheduledMatches = unscheduledMatches.filter(isHandScheduled);

            const trulyUnscheduledMatches = unscheduledMatches.filter((m) => !isHandScheduled(m));

            // Combine them to rebuild timeline
            const allMatchesToPlot = [...existingMatches, ...previouslyScheduledMatches];
            const timeZone = getTournamentTimezone(tournamentInfo?.country, tournamentInfo?.timeZone);
            const manualScheduleGrid = rebuildEntireSchedule(allMatchesToPlot, scheduleConstraints, timeZone);

            const scheduledPayload = allMatchesToPlot.map((m) => {
                const t0 = m.scheduleDate || m.startDate;
                const startMs = new Date(t0!).getTime();

                return {
                    id: m.id,
                    scheduleDate: new Date(startMs),
                    startDate: new Date(startMs),
                    scheduleEndDate: scheduledSlotEndDate(m, scheduleConstraints),
                    court: m.court,
                    courtId: m.court.id,
                };
            });

            // Do not PATCH unlisted matches with nulls — updateTournamentMatchSchedule overwrites
            // every id in the body; bulk nulling wiped all "unscheduled" rows in production.
            const matchesPayload: IMatchScheduleItemDto[] =
                scheduledPayload.filter(isServerBackedScheduleDto);

            try {
                // Save new constraints and current match state to database
                await updateTournamentSchedule(tournamentInfo.id, scheduleConstraints, matchesPayload);

                pushLocalScheduleHistory(dispatch, getState);
                dispatch(setScheduleState(manualScheduleGrid));
                dispatch(setUnscheduledMatchesState(trulyUnscheduledMatches));
                dispatch(setScheduleConstraintsGiven(true));
                dispatch(setLoadedSavedSchedule());

                dispatch(creatingScheduleSuccess());
            } catch (e) {
                const err = e as Error;
                console.error("Failed to save constraints in manual mode", err);
                dispatch(creatingScheduleError(err.message));

                Notification(TypeError.error, "Failed to save constraints to the server.");
            }
        };

export const removeMatchFromScheduleAction =
    (matchId: number, t: TFunction<'translation', undefined>) =>
        async (dispatch: AppDispatch, getState: () => RootState): Promise<boolean> => {
            const prefix = 'creativeDetail-';
            const { scheduleConstraints, schedule, unscheduledMatches, tournamentInfo } = selectSchedule(getState());

            if (!schedule || !scheduleConstraints) return false;

            const allScheduledMatches = dedupeScheduledMatchesById(flattenScheduledMatches(schedule));

            const matchToRemove = allScheduledMatches.find(m => m.id === matchId);
            if (!matchToRemove) return false;

            const keptMatches = allScheduledMatches.filter((m) => m.id !== matchId);
            const timeZone = getTournamentTimezone(tournamentInfo?.country, tournamentInfo?.timeZone);
            const rebuiltSchedule = rebuildEntireSchedule(
                keptMatches,
                scheduleConstraints,
                timeZone
            );

            const matchForUnscheduled = stripMatchToUnscheduled(matchToRemove);
            const updatedUnscheduled = [
                ...unscheduledMatches.filter((m) => m.id !== matchId),
                matchForUnscheduled,
            ];

            pushLocalScheduleHistory(dispatch, getState);
            dispatch(setScheduleState(rebuiltSchedule));
            dispatch(setUnscheduledMatchesState(updatedUnscheduled));
            notifySchedulePlacementInfo(
                dispatch,
                t,
                t(prefix + 'matchUnscheduled', 'Match removed from schedule.')
            );
            return true;
        };

export const applyBulkScheduleTransformAction =
    (
        targetMatchIds: number[],
        ops: BulkAdjustOps,
        t: TFunction<'translation', undefined>,
        existingPreview?: BulkTransformResult
    ) =>
        async (dispatch: AppDispatch, getState: () => RootState): Promise<boolean> => {
            const {
                schedule,
                scheduleConstraints,
                unscheduledMatches,
                tournamentInfo,
                classIdToObject,
            } = selectSchedule(getState());

            if (!schedule || !scheduleConstraints || !hasBulkOpsEffect(ops)) return false;
            if (targetMatchIds.length < MIN_SCHEDULE_BULK_ADJUST) return false;

            const timeZone = getTournamentTimezone(tournamentInfo?.country, tournamentInfo?.timeZone);
            const preview =
                existingPreview ??
                simulateBulkScheduleTransform({
                    schedule,
                    unscheduledMatches,
                    targetMatchIds,
                    ops,
                    constraints: scheduleConstraints,
                    timeZone,
                    classIdToObject,
                    mode: 'apply',
                });

            return commitBulkScheduleTransformFromPreview(
                dispatch,
                getState,
                targetMatchIds,
                preview,
                t
            );
        };

export const applyGroupScheduleDragAction =
    (
        targetMatchIds: number[],
        anchor: { scheduleDate: string; courtId: number },
        targetSlot: IFreeSlotItem,
        t: TFunction<'translation', undefined>,
        blockInvalidPlacement: boolean
    ) =>
        async (dispatch: AppDispatch, getState: () => RootState): Promise<boolean> => {
            const { scheduleConstraints } = selectSchedule(getState());
            if (!scheduleConstraints) return false;

            const ops = computeGroupDragOpsFromFreeSlotDrop(anchor, targetSlot, scheduleConstraints);
            if (!ops || !hasBulkOpsEffect(ops)) return false;

            const {
                schedule,
                unscheduledMatches,
                tournamentInfo,
                classIdToObject,
            } = selectSchedule(getState());
            if (!schedule) return false;

            const timeZone = getTournamentTimezone(
                tournamentInfo?.country,
                tournamentInfo?.timeZone
            );

            let preview: BulkTransformResult | undefined;
            if (blockInvalidPlacement) {
                preview = simulateBulkScheduleTransform({
                    schedule,
                    unscheduledMatches,
                    targetMatchIds,
                    ops,
                    constraints: scheduleConstraints,
                    timeZone,
                    classIdToObject,
                    mode: 'apply',
                });

                const hardSkipped = preview.skipped.filter(
                    (s) => s.reason !== 'not_scheduled' && s.reason !== 'cap'
                );
                if (hardSkipped.length > 0 || preview.courtOverlaps.length > 0) {
                    Notification(
                        TypeError.error,
                        t('creativeDetail-overlapError', 'Not enough time available in this slot.')
                    );
                    return false;
                }
            }

            return dispatch(applyBulkScheduleTransformAction(targetMatchIds, ops, t, preview));
        };

export const bulkRemoveMatchesFromScheduleAction =
    (matchIds: number[], t: TFunction<'translation', undefined>) =>
        async (dispatch: AppDispatch, getState: () => RootState): Promise<boolean> => {
            const prefix = 'creativeDetail-';
            const { scheduleConstraints, schedule, unscheduledMatches, tournamentInfo } =
                selectSchedule(getState());

            if (!schedule || !scheduleConstraints || matchIds.length === 0) return false;

            const allScheduledMatches = dedupeScheduledMatchesById(flattenScheduledMatches(schedule));
            const scheduledIdSet = new Set(allScheduledMatches.map((m) => m.id));
            const scheduledTargetIds = matchIds.filter((id) => scheduledIdSet.has(id));
            if (scheduledTargetIds.length === 0) return false;

            const scheduledTargetSet = new Set(scheduledTargetIds);
            const toRemove = allScheduledMatches.filter((m) => scheduledTargetSet.has(m.id));
            const hasStarted = toRemove.some((m) => !isNotStartedScheduleMatch(m));

            if (scheduledTargetIds.length > 10 || hasStarted) {
                const ok = window.confirm(
                    t(prefix + 'scheduleBulkDeleteConfirm', {
                        count: scheduledTargetIds.length,
                        defaultValue:
                            'Remove {{count}} matches from the schedule? Started matches will be unscheduled.',
                    })
                );
                if (!ok) return false;
            }

            const keptMatches = allScheduledMatches.filter((m) => !scheduledTargetSet.has(m.id));
            const removedFromGrid = toRemove.map((matchToRemove) => ({
                ...matchToRemove,
                scheduleDate: null,
                startDate: null,
                scheduleEndDate: null,
                court: null as unknown as IMatchScheduleItem['court'],
            }));

            const remainingUnscheduled = unscheduledMatches.filter((m) => !scheduledTargetSet.has(m.id));

            const timeZone = getTournamentTimezone(tournamentInfo?.country, tournamentInfo?.timeZone);
            const rebuiltSchedule = rebuildEntireSchedule(
                keptMatches,
                scheduleConstraints,
                timeZone
            );

            pushLocalScheduleHistory(dispatch, getState);
            dispatch(setScheduleState(rebuiltSchedule));
            dispatch(setUnscheduledMatchesState([...remainingUnscheduled, ...removedFromGrid]));
            dispatch(clearScheduleSelection());

            notifySchedulePlacementInfo(
                dispatch,
                t,
                t(prefix + 'scheduleBulkDeleteDone', {
                    count: scheduledTargetIds.length,
                    defaultValue: 'Removed {{count}} matches from the schedule.',
                })
            );
            return true;
        };